import { UtilService } from '../../../shared/services/util.service';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MatDialog } from '@angular/material/dialog';
import {
  PageScrollSpyComponent,
  ScrollSpySection,
} from '../../../shared/components/page-scroll-spy/page-scroll-spy.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { ConfirmAcaoRecorrenteModalComponent } from '../../../shared/components/confirm-acao-recorrente-modal/confirm-acao-recorrente-modal.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ModalRenegociacaoParcelasComponent } from './components/modal-renegociacao-parcelas/modal-renegociacao-parcelas.component';
import { ContasPagarDadosGeraisComponent } from './components/contas-pagar-dados-gerais/contas-pagar-dados-gerais.component';
import { ContasPagarCentroCustoComponent } from './components/contas-pagar-centro-custo/contas-pagar-centro-custo.component';
import { ContasPagarParcelamentoComponent } from './components/contas-pagar-parcelamento/contas-pagar-parcelamento.component';
import { ContasPagarPrevisaoComponent } from './components/contas-pagar-previsao/contas-pagar-previsao.component';
import { ContasPagarComplementaresComponent } from './components/contas-pagar-complementares/contas-pagar-complementares.component';

import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { TipoProcessamentoRecorrenciaEnum } from '../../../shared/enums/financas/tipo-processamento-recorrencia.enum';
import { TelaFinancasEnum } from '../../../shared/enums/financas/tela-financas.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { ContasPagarService } from '../../../shared/services/apis/financas/contas-pagar.service';
import { ConfiguracaoCampoObrigatorioService } from '../../../shared/services/apis/financas/configuracao-campo-obrigatorio.service';
import { GetLookupsContasPagarResponse } from '../../../shared/services/responses/financas/get-lookups-contas-pagar.response';
import { MaskService } from '../../../shared/services/mask.service';
import { CampoObrigatorioStruct } from '../../../shared/services/structs/financas/campo-obrigatorio.struct';
import { MAT_DATE_LOCALE, DateAdapter } from '@angular/material/core';
import { CustomDateAdapter } from '../../../shared/services/custom-date-adapter';

import { ViewChild } from '@angular/core';

@Component({
  selector: 'app-contas-pagar-cadastro',
  standalone: true,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    HeaderComponent,
    FooterActionComponent,
    PageScrollSpyComponent,
    LoadingBoxComponent,
    ContasPagarDadosGeraisComponent,
    ContasPagarCentroCustoComponent,
    ContasPagarParcelamentoComponent,
    ContasPagarPrevisaoComponent,
    ContasPagarComplementaresComponent,
    ConfirmModalComponent,
  ],
  templateUrl: './contas-pagar-cadastro.component.html',
  styleUrl: './contas-pagar-cadastro.component.css',
})
export class ContasPagarCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Financas;
  public funcionalidadeEnum = FuncionalidadeEnum.Financas_Contas_Pagar;

  public form: FormGroup;
  public isEditMode: boolean = false;
  public isLoading: boolean = false;
  public isLoadingLookups: boolean = false;
  public isSaving: boolean = false;
  public idContasPagar: number | null = null;
  public idContasPagarParcela: number | null = null;
  public isAgrupado: boolean = false;
  public desagrupando: boolean = false;
  public qtdParcelas: number = 0;

  // Agrupamento
  public modoAgrupamento: boolean = false;
  public idsParcelasOrigem: number[] = [];

  public lookups: GetLookupsContasPagarResponse | null = null;
  public hasParcelasEmAberto: boolean = false;
  public idNFeEntradaOrigem: number | null = null;
  public idNFSeEntradaOrigem: number | null = null;
  public camposObrigatorios: CampoObrigatorioStruct[] = [];
  private _anexosPendentes: any[] | null = null;

  get isRateioHabilitado(): boolean {
    return this.form.get('dadosGerais.isRateio')?.value === true;
  }

  get tagsControl(): FormControl {
    return this.form.get('tags') as FormControl;
  }

  /**
   * Tags só são editáveis inline quando o documento tem 1 parcela (à vista ou cada documento
   * recorrente). Na criação sempre mostra. Em parcelado (várias parcelas) a edição é por parcela.
   */
  get mostrarTags(): boolean {
    return !this.isEditMode || this.qtdParcelas === 1;
  }

  get valorTotalDocumento(): number {
    return this.form.get('dadosGerais.valorTotal')?.value || 0;
  }

  get rotaVoltar(): string[] {
    if (this.idContasPagarParcela) {
      return ['/financas/contas-pagar/parcela', String(this.idContasPagarParcela)];
    }
    return ['/financas/contas-pagar'];
  }

  private _complementaresComponent!: ContasPagarComplementaresComponent;

  @ViewChild(ContasPagarComplementaresComponent)
  set complementaresComponent(comp: ContasPagarComplementaresComponent) {
    this._complementaresComponent = comp;
    if (comp && this._anexosPendentes) {
      comp.anexos = this._anexosPendentes;
      this._anexosPendentes = null;
    }
  }

  get complementaresComponent(): ContasPagarComplementaresComponent {
    return this._complementaresComponent;
  }

  // Scroll Spy Sections
  get sections(): ScrollSpySection[] {
    const base: ScrollSpySection[] = [
      { id: 'dados-gerais', label: 'Dados Gerais' },
      { id: 'rateio', label: 'Centro de Custo' },
      { id: 'parcelamento', label: 'Parcelamento / Repetição' },
      { id: 'complementos', label: 'Arquivos e Complementos' },
    ];
    if (this.isEditMode && this.qtdParcelas === 1) {
      base.splice(3, 0, { id: 'previsao', label: 'Previsão de Pagamento' });
    }
    return base;
  }

  public isRecorrente: boolean = false;

  public currencyMask: any;
  public percentageMask: any;
  public integerMask: any;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private contasPagarService: ContasPagarService,
    private configuracaoCampoObrigatorioService: ConfiguracaoCampoObrigatorioService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private maskService: MaskService
  ) {
    this.currencyMask = { ...this.maskService.getCurrencyMask(), allowNegative: false };
    this.percentageMask = { ...this.maskService.getDecimalMask2(), allowNegative: false };
    this.integerMask = { ...this.maskService.getIntegerMask(), allowNegative: false };
  }

  ngOnInit(): void {
    this.initForm();
    this.carregarLookups();
    this.carregarCamposObrigatorios();
    this.checkRouteParams();
  }

  private carregarCamposObrigatorios(): void {
    this.configuracaoCampoObrigatorioService.getByTela(TelaFinancasEnum.ContasPagar).subscribe({
      next: res => {
        if (!res.isError && res.listaTelaComCampos?.length) {
          this.camposObrigatorios = res.listaTelaComCampos[0].listaCampos;
          this.aplicarValidadoresDinamicos();
        }
      },
      error: () => {
        /* silent — não bloqueia o formulário */
      },
    });
  }

  public isObrigatorio(nomeTecnico: string): boolean {
    return this.camposObrigatorios.some(c => c.nomeTecnico === nomeTecnico && c.isObrigatorio);
  }

  private aplicarValidadoresDinamicos(): void {
    this.aplicarValidator(this.form.get('parcelamento.idFormaPagamento'), 'id_forma_pagamento');
    this.aplicarValidator(this.form.get('parcelamento.idContaFinanceira'), 'id_conta_financeira');
    this.aplicarValidator(this.form.get('complementos.observacao'), 'observacoes_html');
    // Centro de custo: gerenciado pelo componente filho via @Input isCentroCustoObrigatorio
    // Código de barras: sempre opcional, exibição condicional à forma de pagamento (Boleto)
  }

  private aplicarValidator(control: AbstractControl | null, nomeTecnico: string): void {
    if (!control) return;
    if (this.isObrigatorio(nomeTecnico)) {
      control.setValidators(Validators.required);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  private carregarLookups(): void {
    this.isLoadingLookups = true;
    this.contasPagarService.getLookups().subscribe({
      next: res => {
        this.isLoadingLookups = false;
        if (!res.isError) {
          this.lookups = res;
          this.preSelecionarFormaPagamentoPadrao();
        }
      },
      error: () => {
        this.isLoadingLookups = false;
        this.alertService.show('Erro', 'Não foi possível carregar as listas de seleção.', AlertType.error);
      },
    });
  }

  // Pré-seleciona a forma de pagamento marcada como favorita (isFavorito) só na criação de um lançamento
  // novo — na edição, mantém a forma de pagamento já salva.
  private preSelecionarFormaPagamentoPadrao(): void {
    if (this.isEditMode) return;

    const idFormaPagamentoControl = this.form.get('parcelamento.idFormaPagamento');
    if (!idFormaPagamentoControl || idFormaPagamentoControl.value) return;

    const favorita = this.lookups?.formasPagamento?.find(f => f.isFavorito);
    if (favorita) {
      idFormaPagamentoControl.setValue(favorita.idFormaPagamento);
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      // Estrutura raiz mantida no Pai, os filhos se conectam aqui.
      // 1. Dados Gerais
      dadosGerais: this.fb.group({
        idFornecedor: [null, Validators.required],
        dataEmissao: ['', Validators.required],
        dataCompetencia: ['', Validators.required],
        valorTotal: [null, [Validators.required, Validators.min(0.01)]],
        descricao: ['', Validators.required],
        idCategoriaFinanceira: [null, Validators.required],
        idCentroCusto: [null],
        isRateio: [false],
        numeroDocumento: [''],
      }),

      // 2. Rateio
      rateio: this.fb.group({
        itens: this.fb.array([]),
      }),

      // 3. Parcelamento/Recorrência
      parcelamento: this.fb.group({
        isRecorrente: [false],

        // Dados de parcelamento
        idTipoParcelamento: [null], // ex: À vista, 2x, etc.
        dataVencimento: ['', Validators.required],
        intervaloDiasParcelas: [null],
        idFormaPagamento: [null],
        idContaFinanceira: [null],
        isPago: [false],
        parcelas: this.fb.array([]), // Tabelinha preview

        // Dados de recorrencia
        recorrenciaIntervalo: [null],
        idFrequenciaRecorrencia: [null], // 1=Dia,2=Semana,3=Mes,4=Ano
        recorrenciaDiaVencimento: [null],
        recorrenciaQuantidade: [null],
      }),

      // 4. Previsão de Pagamento
      previsao: this.fb.group({
        datetimePrevisao: [''],
        valorJuros: [null],
        valorMulta: [null],
        valorDesconto: [null],
        valorAcrescimo: [null],
      }),

      // 5. Complementos
      complementos: this.fb.group({
        observacao: [''],
        codigoBarras: [''],
        chavePix: [''],
      }),

      // 6. Tags (aplicadas a todas as parcelas na criação; por parcela na edição)
      tags: [[]],
    });
  }

  private checkRouteParams(): void {
    // Parâmetros matriz: /cadastrar;idContasPagar=123
    const paramIdConta = this.route.snapshot.paramMap.get('idContasPagar');
    // Pode vir com a parcela para edição específica
    const paramIdParcela = this.route.snapshot.paramMap.get('idContasPagarParcela');

    if (paramIdConta) {
      this.isEditMode = true;
      this.idContasPagar = Number(paramIdConta);

      if (paramIdParcela) {
        this.idContasPagarParcela = Number(paramIdParcela);
      }

      this.loadData();
    }

    // Agrupamento via queryParams
    const qp = this.route.snapshot.queryParamMap;
    if (qp.get('modoAgrupamento') === 'true') {
      this.modoAgrupamento = true;
      const idFornecedor = Number(qp.get('idFornecedor') || 0);
      const valorTotal = Number(qp.get('valorTotal') || 0);
      const idsStr = qp.get('idsParcelasOrigem');
      this.idsParcelasOrigem = idsStr ? JSON.parse(idsStr) : [];

      // Pré-preencher dados gerais
      this.form.get('dadosGerais.idFornecedor')?.setValue(idFornecedor);
      this.form.get('dadosGerais.valorTotal')?.setValue(valorTotal);
      this.form.get('dadosGerais.descricao')?.setValue('Documento Consolidado (Agrupamento)');
      this.form.get('dadosGerais.dataEmissao')?.setValue(new Date());
      this.form.get('dadosGerais.dataCompetencia')?.setValue(new Date());

      // Agrupamento não pode ser recorrente
      this.form.get('parcelamento.isRecorrente')?.setValue(false);
      this.form.get('parcelamento.isRecorrente')?.disable();
    }
  }

  private loadData(): void {
    if (!this.idContasPagar) return;

    this.isLoading = true;
    this.contasPagarService.getById(this.idContasPagar).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show(
            'Erro',
            res.errorDescription || 'Erro ao carregar os dados.',
            AlertType.error
          );
          this.router.navigate(['/financas/contas-pagar']);
          return;
        }

        if (res) {
          const dados = res; // A Resposta é o proprio objeto ContasPagarGetByIdResponse

           this.qtdParcelas = dados.parcelas?.length ?? 0;
           this.isAgrupado = dados.isAgrupado ?? false;
           this.idNFeEntradaOrigem = dados.idNFeEntrada ?? null;
           this.idNFSeEntradaOrigem = dados.idNFSeEntrada ?? null;

          // Verificar se há parcelas em aberto para habilitar botão de renegociação
          if (dados.parcelas && dados.parcelas.length > 0) {
            this.hasParcelasEmAberto = dados.parcelas.some((p: any) => p.idStatusContasPagar === 1);
          }

          // Popula FormArray de parcelas quando há múltiplas (parcelado)
          if (dados.parcelas && dados.parcelas.length > 1) {
            const parcelasFormArray = this.form.get(
              'parcelamento.parcelas'
            ) as import('@angular/forms').FormArray;
            parcelasFormArray.clear();
            dados.parcelas.forEach((p: any) => {
              parcelasFormArray.push(
                this.fb.group({
                  idContasPagarParcela: [p.idContasPagarParcela],
                  nroParcela: [p.nroParcela],
                  dataVencimento: [p.datetimeVencimento ? new Date(p.datetimeVencimento) : null],
                  valor: [p.valor],
                  descricao: [p.descricao],
                })
              );
            });
          }

          // 1. Dados Gerais
          this.form.get('dadosGerais')?.patchValue({
            idFornecedor: dados.idPessoaFornecedor,
            dataEmissao: dados.dataEmissao ? new Date(dados.dataEmissao) : '',
            dataCompetencia: dados.dataCompetencia ? new Date(dados.dataCompetencia) : '',
            valorTotal: dados.valorTotal,
            descricao: dados.descricaoOriginal,
            idCategoriaFinanceira: dados.idCategoriaFinanceira,
            isRateio: dados.hasRateio,
            numeroDocumento: dados.numeroDocumento,
          });

          // 2. Rateio / Centro de Custo
          if (dados.hasRateio) {
            const itensArray = this.form.get('rateio.itens') as import('@angular/forms').FormArray;
            itensArray.clear();

            if (dados.rateios && dados.rateios.length > 0) {
              dados.rateios.forEach((r: any) => {
                itensArray.push(
                  this.fb.group({
                    idCentroCusto: [r.idCentroCusto, Validators.required],
                    valor: [r.valor, [Validators.min(0)]],
                    percentual: [r.percentual, [Validators.min(0), Validators.max(100)]],
                  })
                );
              });
            }
          } else if (dados.rateios && dados.rateios.length === 1) {
            // Centro de custo único (sem rateio): extrair do array de rateios
            this.form.get('dadosGerais.idCentroCusto')?.setValue(dados.rateios[0].idCentroCusto);
          }

          // 3. Parcelamento / Recorrencia / Complementos Baseado na 1a parcela se houver
          if (dados.parcelas && dados.parcelas.length > 0) {
            // Se houver uma parcela especifica passada na rota (edição pontual), usar ela. Senão considerar a primeira.
            const pAlvo = this.idContasPagarParcela
              ? dados.parcelas.find((p: any) => p.idContasPagarParcela === this.idContasPagarParcela)
              : dados.parcelas[0];

            if (pAlvo) {
              // Preencher Previsao
              this.form.get('previsao')?.patchValue({
                datetimePrevisao: pAlvo.datetimePrevisao ? new Date(pAlvo.datetimePrevisao) : '',
                valorJuros: pAlvo.valorJurosPrevisto || null,
                valorMulta: pAlvo.valorMultaPrevisto || null,
                valorDesconto: pAlvo.valorDescontoPrevisto || null,
                valorAcrescimo: pAlvo.valorAcrescimoPrevisto || null,
              });

              // Preencher Parcelamento (parcela usa datetimeVencimento)
              this.form.get('parcelamento')?.patchValue({
                idTipoParcelamento: dados.idTipoParcelamento,
                dataVencimento: pAlvo.datetimeVencimento ? new Date(pAlvo.datetimeVencimento) : '',
                intervaloDiasParcelas: dados.intervaloDiasParcelas,
                idFormaPagamento: pAlvo.idFormaPagamento,
                idContaFinanceira: pAlvo.idContaFinanceira,
                isPago: pAlvo.isPago,
              });

              // Preencher Complementos
              this.form.get('complementos')?.patchValue({
                observacao: pAlvo.observacoesHtml || '',
                codigoBarras: pAlvo.codigoBarrasLinhaDigitavel || '',
                chavePix: pAlvo.chavePix || '',
              });

              // Tags da parcela alvo (relevante quando há 1 parcela — edição inline)
              this.form.get('tags')?.setValue(pAlvo.tags ?? []);
            }
          }

          // Anexos do documento — guarda em _anexosPendentes; o setter do ViewChild aplica quando o componente renderizar
          if (dados.anexosDocumento && dados.anexosDocumento.length > 0) {
            this._anexosPendentes = dados.anexosDocumento.map((a: any) => ({
              idContasPagarAnexo: a.idContasPagarAnexo,
              nomeArquivo: a.nomeArquivo || 'arquivo',
              tamanhoBytes: a.tamanhoBytes || 0,
              urlArquivo: a.urlArquivo,
            }));
          }

          // Se for recorrente, desabilitamos a edição de recorrencia na UI (Escopo inicial de apenas visibilidade)
          if (dados.idConfiguracaoRecorrenciaPagar > 0) {
            this.isRecorrente = true;
            this.form.get('parcelamento.isRecorrente')?.setValue(true);
            if (dados.recorrenciaInfo) {
              this.form.get('parcelamento.recorrenciaIntervalo')?.setValue(dados.recorrenciaInfo.intervalo);
              this.form
                .get('parcelamento.idFrequenciaRecorrencia')
                ?.setValue(dados.recorrenciaInfo.idFrequenciaRecorrencia);
              this.form
                .get('parcelamento.recorrenciaQuantidade')
                ?.setValue(dados.recorrenciaInfo.quantidadeVezes);
              this.form
                .get('parcelamento.recorrenciaDiaVencimento')
                ?.setValue(dados.recorrenciaInfo.diaVencimentoFixo);
            }
          }
        }
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar a conta a pagar.', AlertType.error);
        this.router.navigate(['/financas/contas-pagar']);
      },
    });
  }

  public salvar(): void {
    if (this.desagrupando) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertService.show('Atenção', 'Verifique os campos em vermelho.', AlertType.warning);
      return;
    }

    // Helper functions
    const dGerais = this.form.get('dadosGerais')?.value;
    const rateio = this.form.get('rateio')?.value;
    const parc = this.form.get('parcelamento')?.value;

    if (dGerais.dataEmissao && parc.dataVencimento && new Date(parc.dataVencimento) < new Date(dGerais.dataEmissao)) {
      this.alertService.show('Atenção', 'A data de vencimento não pode ser anterior à data de emissão.', AlertType.warning);
      return;
    }

    if (!parc.isRecorrente && parc.parcelas && parc.parcelas.length > 0) {
      const somaParcelas = parc.parcelas.reduce((soma: number, p: any) => soma + (p.valor || 0), 0);
      if (UtilService.arredondarAbnt(somaParcelas, 2) !== UtilService.arredondarAbnt(dGerais.valorTotal || 0, 2)) {
        this.alertService.show('Atenção', 'A soma das parcelas precisa ser igual ao Valor Total do documento.', AlertType.warning);
        return;
      }
    }

    if (dGerais.isRateio) {
      const itensRateio: any[] = rateio.itens || [];

      if (!itensRateio.length) {
        this.alertService.show('Atenção', 'Adicione ao menos uma linha de rateio por centro de custo.', AlertType.warning);
        return;
      }

      const possuiLinhaInvalida = itensRateio.some(
        item => !item.idCentroCusto || (!item.valor && !item.percentual)
      );
      if (possuiLinhaInvalida) {
        this.alertService.show('Atenção', 'Preencha centro de custo e valor ou percentual em todas as linhas do rateio.', AlertType.warning);
        return;
      }

      const somaPercentual = itensRateio.reduce((soma: number, item: any) => soma + (Number(item.percentual) || 0), 0);
      if (Math.abs(somaPercentual - 100) > 0.01) {
        this.alertService.show('Atenção', 'O rateio por centro de custo precisa totalizar 100%.', AlertType.warning);
        return;
      }
    }

    this.isSaving = true;
    const prev = this.form.get('previsao')?.value;
    const comp = this.form.get('complementos')?.value;

    const dtEmissao = dGerais.dataEmissao ? new Date(dGerais.dataEmissao).toISOString() : '';
    const dtComp = dGerais.dataCompetencia ? new Date(dGerais.dataCompetencia).toISOString() : '';
    const dtVenc = parc.dataVencimento ? new Date(parc.dataVencimento).toISOString() : '';
    const dtPrev = prev.datetimePrevisao ? new Date(prev.datetimePrevisao).toISOString() : null;

    if (this.isEditMode) {
      // Se for recorrente, abre modal para escolher escopo da edição
      if (this.isRecorrente) {
        this.isSaving = false;
        const dialogRef = this.dialog.open(ConfirmAcaoRecorrenteModalComponent, {
          width: '500px',
          data: {
            title: 'Atenção, Conta Recorrente!',
            description:
              'Você está editando uma conta a pagar que possui recorrência. O que deseja fazer com as contas em aberto vinculadas a essa conta?',
            buttonConfirm: 'Salvar',
            buttonClose: 'Cancelar',
          },
        });

        dialogRef.afterClosed().subscribe((result: any) => {
          if (result?.confirm) {
            this.executarUpdate(
              result.tipoProcessamento,
              dGerais,
              rateio,
              parc,
              prev,
              comp,
              dtEmissao,
              dtComp,
              dtVenc,
              dtPrev
            );
          }
        });
      } else {
        this.executarUpdate(
          TipoProcessamentoRecorrenciaEnum.ApenasEsta,
          dGerais,
          rateio,
          parc,
          prev,
          comp,
          dtEmissao,
          dtComp,
          dtVenc,
          dtPrev
        );
      }
    } else {
      // Cadastrar Novo
      const requestCreate: any = {
        idPessoaFornecedor: dGerais.idFornecedor,
        dataEmissao: dtEmissao,
        dataCompetencia: dtComp,
        valorTotal: dGerais.valorTotal,
        descricaoOriginal: dGerais.descricao,
        numeroDocumento: dGerais.numeroDocumento,
        idCategoriaFinanceira: dGerais.idCategoriaFinanceira,
        hasRateio: dGerais.isRateio,
        idCentroCusto: !dGerais.isRateio ? dGerais.idCentroCusto : null,
        rateios: dGerais.isRateio && rateio.itens.length > 0 ? rateio.itens : null,

        isRecorrente: parc.isRecorrente,
        observacoesHtml: comp.observacao,
        codigoBarrasLinhaDigitavel: comp.codigoBarras,
        chavePix: comp.chavePix,
        tags: this.form.get('tags')?.value ?? [],
        anexos:
          this.complementaresComponent?.anexos?.map(a => ({
            idContasPagarAnexo: a.idContasPagarAnexo || null,
            nomeArquivo: a.nomeArquivo,
            tamanhoBytes: a.tamanhoBytes,
            base64: a.base64 || null,
          })) || [],
      };

      // Se for recorrente, anexa objeto RecorrenciaRequest
      if (parc.isRecorrente) {
        requestCreate.recorrencia = {
          intervalo: parc.recorrenciaIntervalo || 1,
          idFrequenciaRecorrencia: parc.idFrequenciaRecorrencia || 3, // default mes
          diaVencimentoFixo: parc.recorrenciaDiaVencimento || 0,
          quantidadeVezes: parc.recorrenciaQuantidade || 1,
          idFormaPagamento: parc.idFormaPagamento,
          idContaFinanceira: parc.idContaFinanceira,
          dataInicio: dtVenc,
        };
      } else {
        // Se nao for recorrente, anexa objeto ParcelamentoRequest
        const parcelamentoObj: any = {
          idTipoParcelamento: parc.idTipoParcelamento || 1,
          dataVencimento: dtVenc,
          intervaloDiasParcelas: parc.intervaloDiasParcelas,
          idFormaPagamento: parc.idFormaPagamento,
          idContaFinanceira: parc.idContaFinanceira,
          isPago: parc.isPago ? true : false,
        };

        // Se o grid de simulação foi preenchido, enviar parcelas personalizadas
        const parcelasArray = this.form.get('parcelamento.parcelas') as import('@angular/forms').FormArray;
        if (parcelasArray && parcelasArray.length > 0) {
          parcelamentoObj.parcelas = parcelasArray.controls.map((ctrl: any) => ({
            nroParcela: ctrl.get('nroParcela')?.value,
            dataVencimento: ctrl.get('dataVencimento')?.value
              ? new Date(ctrl.get('dataVencimento').value).toISOString()
              : null,
            valor: ctrl.get('valor')?.value || 0,
            descricao: ctrl.get('descricao')?.value,
          }));
        }

        requestCreate.parcelamento = parcelamentoObj;
      }

      // Determinar se é agrupamento ou criação normal
      if (this.modoAgrupamento) {
        const agrupamentoRequest = {
          documento: requestCreate,
          idsParcelasOrigem: this.idsParcelasOrigem,
          observacaoAgrupamento: comp.observacao || null,
        };

        this.contasPagarService.agrupar(agrupamentoRequest).subscribe({
          next: res => {
            this.isSaving = false;
            if (res.isError) {
              this.alertService.show(
                'Erro',
                res.errorDescription || 'Erro ao agrupar documentos',
                AlertType.error
              );
              return;
            }
            this.alertService.show(
              'Sucesso',
              'Parcelas agrupadas com sucesso! As parcelas originais foram canceladas.',
              AlertType.success
            );
            this.router.navigate(['/financas/contas-pagar']);
          },
          error: () => {
            this.isSaving = false;
            this.alertService.show(
              'Erro',
              'Ocorreu um erro ao comunicar-se com o servidor.',
              AlertType.error
            );
          },
        });
      } else {
        this.contasPagarService.create(requestCreate).subscribe({
          next: res => {
            this.isSaving = false;
            if (res.isError) {
              this.alertService.show(
                'Erro',
                res.errorDescription || 'Erro ao inserir o documento',
                AlertType.error
              );
              return;
            }
            this.alertService.show('Sucesso', 'Documento inserido com sucesso!', AlertType.success);
            this.router.navigate(['/financas/contas-pagar']);
          },
          error: () => {
            this.isSaving = false;
            this.alertService.show(
              'Erro',
              'Ocorreu um erro ao comunicar-se com o servidor.',
              AlertType.error
            );
          },
        });
      }
    }
  }

  private executarUpdate(
    tipoEdicaoRecorrencia: number,
    dGerais: any,
    rateio: any,
    parc: any,
    prev: any,
    comp: any,
    dtEmissao: string,
    dtComp: string,
    dtVenc: string,
    dtPrev: string | null
  ): void {
    this.isSaving = true;

    const requestUpdate: any = {
      idContasPagar: this.idContasPagar!,
      idContasPagarParcela: this.idContasPagarParcela,
      tipoEdicaoRecorrencia: tipoEdicaoRecorrencia,
      idPessoaFornecedor: dGerais.idFornecedor,
      dataEmissao: dtEmissao,
      dataCompetencia: dtComp,
      valorTotal: dGerais.valorTotal,
      descricaoOriginal: dGerais.descricao,
      numeroDocumento: dGerais.numeroDocumento,
      idCategoriaFinanceira: dGerais.idCategoriaFinanceira,
      hasRateio: dGerais.isRateio,
      idCentroCusto: !dGerais.isRateio ? dGerais.idCentroCusto : null,
      rateios: dGerais.isRateio && rateio.itens.length > 0 ? rateio.itens : null,
      dataVencimento: dtVenc,
      datetimePrevisao: dtPrev,
      idFormaPagamento: parc.idFormaPagamento,
      idContaFinanceira: parc.idContaFinanceira,
      valorJurosPrevisto: prev.valorJuros,
      valorMultaPrevisto: prev.valorMulta,
      valorDescontoPrevisto: prev.valorDesconto,
      valorAcrescimoPrevisto: prev.valorAcrescimo,
      isPago: parc.isPago ? true : false,
      observacoesHtml: comp.observacao,
      codigoBarrasLinhaDigitavel: comp.codigoBarras,
      chavePix: comp.chavePix,
      // Só envia tags quando editáveis inline (1 parcela). Em parcelado, null = backend não mexe nas tags.
      tags: this.mostrarTags ? (this.form.get('tags')?.value ?? []) : null,
      anexos:
        this.complementaresComponent?.anexos?.map(a => ({
          idContasPagarAnexo: a.idContasPagarAnexo || null,
          nomeArquivo: a.nomeArquivo,
          tamanhoBytes: a.tamanhoBytes,
          base64: a.base64 || null,
        })) || [],
    };

    this.contasPagarService.update(requestUpdate).subscribe({
      next: res => {
        this.isSaving = false;
        if (res.isError) {
          this.alertService.show(
            'Erro',
            res.errorDescription || 'Erro ao atualizar o documento',
            AlertType.error
          );
          return;
        }
        this.alertService.show('Sucesso', 'Documento atualizado com sucesso!', AlertType.success);
        this.router.navigate(this.rotaVoltar);
      },
      error: () => {
        this.isSaving = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao comunicar-se com o servidor.', AlertType.error);
      },
    });
  }

  public abrirRenegociacao(): void {
    if (!this.idContasPagar) return;

    // Buscar valor em aberto do backend
    this.contasPagarService.getValorRenegociacao(this.idContasPagar).subscribe({
      next: (res: any) => {
        if (res.isError) {
          this.alertService.show(
            'Erro',
            res.errorDescription || 'Erro ao buscar valor para renegociação.',
            AlertType.error
          );
          return;
        }

        const dialogRef = this.dialog.open(ModalRenegociacaoParcelasComponent, {
          maxWidth: '95vw',
          maxHeight: '95vh',
          disableClose: true,
          data: {
            idContasPagar: this.idContasPagar,
            valorEmAberto: res.valorEmAberto,
            valorTotal: res.valorTotal,
            descricaoDocumento: this.form.get('dadosGerais.descricao')?.value || '',
            listaTiposParcelamento: this.lookups?.tiposParcelamento ?? [],
            listaContaFinanceira: this.lookups?.contasFinanceiras ?? [],
            listaFormaPagamento: this.lookups?.formasPagamento ?? [],
            currencyMask: this.currencyMask,
          },
        });

        dialogRef.afterClosed().subscribe((result: any) => {
          if (result?.success) {
            // Recarregar os dados do documento após renegociação
            this.loadData();
          }
        });
      },
      error: () => {
        this.alertService.show('Erro', 'Erro ao comunicar-se com o servidor.', AlertType.error);
      },
    });
  }

  public desagrupar(): void {
    if (!this.idContasPagar) return;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Desagrupar este lançamento?',
        innerHtml: 'Ao continuar, este lançamento consolidado será <b>excluído</b> e as <b>parcelas originais voltarão a ficar em aberto</b>.',
        buttonConfirm: 'Sim, desagrupar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result?.confirm) return;

      this.desagrupando = true;
      this.contasPagarService.desagrupar(this.idContasPagar!).subscribe({
        next: (res) => {
          this.desagrupando = false;
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription || 'Erro ao desagrupar.', AlertType.error);
            return;
          }
          this.alertService.show('Sucesso', 'Agrupamento desfeito com sucesso.', AlertType.success);
          this.router.navigate(this.rotaVoltar);
        },
        error: () => {
          this.desagrupando = false;
          this.alertService.show('Erro', 'Erro ao comunicar-se com o servidor.', AlertType.error);
        },
      });
    });
  }

  public cancelar(): void {
    this.router.navigate(this.rotaVoltar);
  }
}
